import React, { useState, useEffect, useRef } from 'react';

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type'> {
  id: string;
  name: string;
  value: number | string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: string;
  min?: number;
  max?: number;
  step?: number | string;
  precision?: number;
  className?: string;
}

/**
 * Standard number input component with consistent styling
 */
const NumberInput: React.FC<NumberInputProps> = ({
  id,
  name,
  value,
  onChange,
  label,
  min,
  max,
  step = '1',
  precision,
  className = '',
  ...props
}) => {
  // Keep track of the input state to allow for more flexible editing
  const [inputValue, setInputValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isInitialFocus, setIsInitialFocus] = useState<boolean>(true);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Update internal inputValue when the external value changes
  useEffect(() => {
    // Only update the display value if not focused to avoid cursor position issues
    if (!isFocused) {
      if (value === '' || value === null || value === undefined) {
        setInputValue('');
      } else {
        const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
        const formattedValue = precision !== undefined ? numValue.toFixed(precision) : numValue.toString();
        setInputValue(formattedValue);
      }
    }
  }, [value, precision, isFocused]);

  // Handle the input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Allow empty input field during typing
    if (newValue === '' || newValue === '-' || newValue === '.') {
      // Don't update parent state yet - keep the empty input for better UX
      return;
    }
    
    onChange(e);
  };
  
  // Handle focus - clear the value only when it's 0
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    
    // Only if this is initial value (0, 0.00, etc.) and first focus, clear the input
    if (isInitialFocus && (value === 0 || value === '0')) {
      setInputValue('');
      setIsInitialFocus(false);
    }
    
    if (props.onFocus) {
      props.onFocus(e);
    }
  };
  
  // Handle blur - format the value back with proper precision
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    
    // If input is empty, revert to 0
    if (inputValue === '' || inputValue === '-' || inputValue === '.') {
      const formattedZero = precision !== undefined ? (0).toFixed(precision) : '0';
      setInputValue(formattedZero);
      
      // Create a synthetic event to pass 0 to parent
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: '0',
          name: e.target.name
        }
      } as React.ChangeEvent<HTMLInputElement>;
      
      onChange(syntheticEvent);
    } 
    // Otherwise format the existing value
    else if (precision !== undefined) {
      // Format with precision but don't change the actual value
      const numValue = typeof value === 'string' ? parseFloat(value) || 0 : value;
      setInputValue(numValue.toFixed(precision));
    }
    
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  return (
    <div className="w-full">
      {label && <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input
        ref={inputRef}
        type="number"
        id={id}
        name={name}
        value={inputValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        min={min}
        max={max}
        step={step}
        className={`block w-full px-3 py-2 border border-primary-silver rounded-md shadow-sm focus:outline-none focus:ring-primary-charcoal focus:border-primary-charcoal sm:text-sm ${className}`}
        {...props}
      />
    </div>
  );
};

export default NumberInput; 